const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const stringValue = `${value}`.trim();
  if (!stringValue) return null;
  return /^-?\d+$/.test(stringValue) ? parseInt(stringValue, 10) : stringValue;
};

const normalizeText = (value) => `${value || ""}`.trim();

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const EMPTY_FULFILLMENT_ADDRESS = Object.freeze({
  type: null,
  block: "",
  street: "",
  avenue: "",
  building: "",
  floor: "",
  apartment: "",
  additional: "",
  nationalAddress: "",
  lat: null,
  lng: null,
  gpsConfirmed: false,
});

const sameContextId = (left, right) =>
  normalizeId(left) === normalizeId(right);

export const hasFulfillmentLocationChanged = (current = {}, next = {}) =>
  current.type !== next.type ||
  !sameContextId(current.countryId, next.countryId) ||
  !sameContextId(current.areaId, next.areaId) ||
  !sameContextId(current.branchId, next.branchId);

export const invalidateAddressForLocationChange = (current, next = {}) =>
  hasFulfillmentLocationChanged(current, next)
    ? { ...next, address: { ...EMPTY_FULFILLMENT_ADDRESS } }
    : next;

const resolveFulfillmentType = (address, branch) => {
  if (address?.selectedDeliveryType === 0 || address?.selectedDeliveryType === "0")
    return "delivery";
  if (address?.selectedDeliveryType === 1 || address?.selectedDeliveryType === "1")
    return "pickup";
  if (branch?.isPickup === true) return "pickup";
  if (branch?.bdc) return "delivery";
  return null;
};

const resolvePickupMode = (type, address, settings) => {
  if (type !== "pickup") return null;
  const explicitMode = `${
    address?.pickupMode || address?.pickup_mode || ""
  }`.trim().toLowerCase();
  if (["inside", "car"].includes(explicitMode)) return explicitMode;
  if (address?.carPickup === true || address?.car_pickup === true) return "car";
  if (
    settings?.enable_car_qr &&
    (address?.parkingNumber ||
      address?.make ||
      address?.color ||
      address?.plateNumber)
  )
    return "car";
  return null;
};

export const normalizeFulfillmentContext = ({
  address = {},
  branch = {},
  deliveryTime = {},
  settings = {},
} = {}) => {
  const type = resolveFulfillmentType(address, branch);
  const isPickup = type === "pickup";
  const pickupMode = resolvePickupMode(type, address, settings);
  const branchId = normalizeId(
    branch?.branch !== null && branch?.branch !== undefined
      ? branch.branch
      : address?.pickupArea,
  );

  return {
    version: 1,
    type,
    isPickup,
    pickupMode,
    isBranchBased: normalizeBoolean(settings?.is_branch_based_ordering),
    countryId: type === "delivery" ? normalizeId(address?.country) : null,
    areaId: type === "delivery" ? normalizeId(address?.area) : null,
    branchId,
    branchDeliveryChargeId:
      type === "delivery" ? normalizeId(branch?.bdc) : null,
    address: {
      type: normalizeId(address?.selectedAddressType),
      block: normalizeText(address?.block),
      street: normalizeText(address?.street),
      avenue: normalizeText(address?.avenue),
      building: normalizeText(address?.building),
      floor: normalizeText(address?.floor),
      apartment: normalizeText(address?.apartment),
      additional: normalizeText(address?.additional),
      nationalAddress: normalizeText(address?.paci),
      lat: normalizeCoordinate(address?.lat),
      lng: normalizeCoordinate(address?.lng),
      gpsConfirmed: normalizeBoolean(address?.gpsConfirmed),
    },
    time: {
      type:
        deliveryTime?.type === "asap" || deliveryTime?.type === "scheduled"
          ? deliveryTime.type
          : null,
      date: normalizeText(deliveryTime?.expected_date),
      start: normalizeText(deliveryTime?.expected_start),
      end: normalizeText(deliveryTime?.expected_end),
      withinMinutes:
        deliveryTime?.within === null ||
        deliveryTime?.within === undefined ||
        deliveryTime?.within === "" ||
        !Number.isFinite(Number(deliveryTime.within))
          ? null
          : Math.max(0, Number(deliveryTime.within)),
      authority: normalizeText(
        deliveryTime?.asap_authority || deliveryTime?.authority,
      ),
    },
    vehicle: {
      make: normalizeText(address?.make),
      color: normalizeText(address?.color),
      plateNumber: normalizeText(address?.plateNumber),
      parkingSpot: normalizeText(address?.ebutlerRow),
    },
    qr: {
      tableToken: normalizeText(address?.token),
      parkingNumber: normalizeText(address?.parkingNumber),
      dineInOrderId: normalizeText(address?.dineInOrderID),
      dineInSessionId: normalizeText(address?.dineInSessionID),
    },
  };
};

const canonicalContext = (context) => {
  const address = context.address || {};
  const time = context.time || {};
  const vehicle = context.vehicle || {};
  const qr = context.qr || {};
  return [
    context.version,
    context.type,
    context.pickupMode,
    context.isBranchBased,
    context.countryId,
    context.areaId,
    context.branchId,
    context.branchDeliveryChargeId,
    address.type,
    address.block,
    address.street,
    address.avenue,
    address.building,
    address.floor,
    address.apartment,
    address.additional,
    address.nationalAddress,
    address.lat,
    address.lng,
    address.gpsConfirmed,
    time.type,
    time.date,
    time.start,
    time.end,
    time.authority,
    vehicle.make,
    vehicle.color,
    vehicle.plateNumber,
    vehicle.parkingSpot,
    qr.parkingNumber,
    qr.tableToken,
    qr.dineInOrderId,
    qr.dineInSessionId,
  ];
};

const fnv1a = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const createFulfillmentFingerprint = (input = {}) => {
  const context = input.version
    ? input
    : normalizeFulfillmentContext(input);
  return `fc1-${fnv1a(JSON.stringify(canonicalContext(context)))}`;
};

export const getFulfillmentIssues = (
  context,
  {
    requireAddress = false,
    requireTime = false,
    requireGps = false,
    carRequirements = {},
  } = {},
) => {
  const issues = [];
  if (!context?.type) issues.push("fulfillment_type");

  if (context?.type === "delivery") {
    if (!context.countryId) issues.push("country");
    if (!context.areaId) issues.push("area");
    if (context.isBranchBased && !context.branchId) issues.push("branch");
    if (context.isBranchBased && !context.branchDeliveryChargeId)
      issues.push("branch_delivery_charge");
    if (requireAddress) {
      if (!context.address.block) issues.push("address_block");
      if (!context.address.street) issues.push("address_street");
      if (!context.address.building) issues.push("address_building");
    }
    if (
      requireGps &&
      (!context.address.gpsConfirmed ||
        context.address.lat === null ||
        context.address.lng === null)
    )
      issues.push("address_gps");
  }

  if (context?.type === "pickup" && !context.branchId) issues.push("branch");
  if (
    context?.type === "pickup" &&
    carRequirements.requirePickupMode &&
    !context.pickupMode
  )
    issues.push("pickup_mode");

  if (
    requireTime &&
    (!context?.time?.type ||
      !context.time.date ||
      !context.time.start ||
      !context.time.end)
  )
    issues.push("fulfillment_time");

  if (context?.type === "pickup" && context.pickupMode === "car") {
    if (carRequirements.make && !context.vehicle.make) issues.push("car_make");
    if (carRequirements.color && !context.vehicle.color)
      issues.push("car_color");
    if (carRequirements.plateNumber && !context.vehicle.plateNumber)
      issues.push("car_plate_number");
  }

  return Array.from(new Set(issues));
};

export const isFulfillmentContextComplete = (context, options) =>
  getFulfillmentIssues(context, options).length === 0;

export const isBrowsingFulfillmentContextResolved = (input = {}) => {
  const context = input.version ? input : normalizeFulfillmentContext(input);
  if (!context.type) return false;
  if (context.type === "pickup") return Boolean(context.branchId);
  if (context.isBranchBased)
    return Boolean(context.branchId && context.branchDeliveryChargeId);
  return Boolean(context.areaId);
};
